import { useEffect, useRef, RefObject } from "react";
import { useInView } from "react-intersection-observer";
import { HashTagPage } from "../../../types/ComponentProps";
import Spinner from "../../spinner/Spinner";
import useGetAllHashTag from "../../../hooks/useGetAllHashTag";
import HashTagButtonStyles from "./HashTagButtonStyles.module.css";
import { usePostForm } from "../../../context/PostFormContext";
import { MentionInputHandle } from "../mentionInput/MentionInput";

type HashTagButtonProps = {
  isOpen: boolean;
  toggleDropdown: () => void;
  mentionInputRef: RefObject<MentionInputHandle>;
};

const HashTagButton = ({
  isOpen,
  toggleDropdown,
  mentionInputRef,
}: HashTagButtonProps) => {
  const { formPost } = usePostForm();
  const { ref, inView } = useInView();
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    getAllDataHashTag,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAllHashTag();

  const selectedHashTagIds = new Set(
    formPost?.hashTagIds?.map((tag: any) => tag.hashTagId),
  );

  const filteredHashTags =
    getAllDataHashTag?.pages
      .flatMap((page: HashTagPage) => page.hashTags)
      .filter((hashTag: any) => !selectedHashTagIds.has(hashTag.hashTagId)) ||
    [];

  const handleSelectHashtag = (data: any) => {
    mentionInputRef.current?.appendHashtagChip({
      hashTagId: data.hashTagId,
      hashTagName: data.hashTagName,
    });
    toggleDropdown();
  };

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        toggleDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleDropdown]);

  return (
    <div className={HashTagButtonStyles.containerBlk} ref={containerRef}>
      <button
        onClick={toggleDropdown}
        className={HashTagButtonStyles.buttonBlk}
      >
        <i className="fa-solid fa-hashtag"></i> HashTag
      </button>

      {isOpen && (
        <div className={HashTagButtonStyles.hashTagDropDown}>
          {isLoading ? (
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          ) : (
            <div
              className={HashTagButtonStyles.hashTagDropdownScrollBlk}
              style={
                filteredHashTags.length <= 4
                  ? { overflow: "hidden" }
                  : { overflowY: "scroll" }
              }
            >
              {filteredHashTags.length === 0 ? (
                <div className={HashTagButtonStyles.noHashtagMsg}>
                  No hashtags left to select
                </div>
              ) : (
                filteredHashTags.map((data: any) => (
                  <div
                    key={data.hashTagId}
                    className={HashTagButtonStyles.hashTagItem}
                    onClick={() => handleSelectHashtag(data)}
                  >
                    #{data.hashTagName}
                  </div>
                ))
              )}

              {isFetchingNextPage && (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              )}
            </div>
          )}
          <div ref={ref}></div>
        </div>
      )}
    </div>
  );
};

export default HashTagButton;
